// ============================================
// ConverseTech Frontend - Session Store
// ============================================

import { create } from 'zustand';
import type { SessionState, TrainingMode, TranscriptUpdate, SessionMetrics, DETPhase, DETPhaseScore } from '@conversetech/shared';
import { UI_CONFIG } from '@conversetech/shared';

interface SessionStore {
  // Session state
  sessionId: string | null;
  wsUrl: string | null;
  mode: TrainingMode | null;
  state: SessionState;
  isActive: boolean;

  // Recording state
  isRecording: boolean;
  isMuted: boolean;

  // Transcript
  transcript: TranscriptUpdate[];

  // Metrics
  metrics: SessionMetrics | null;

  // DET-specific state
  detPhase: DETPhase | null;
  detPhaseStartTime: number | null;
  detPhaseOverTime: boolean;
  detScores: DETPhaseScore[];

  // Actions
  setSession: (sessionId: string, wsUrl: string, mode: TrainingMode) => void;
  setState: (state: SessionState) => void;
  setActive: (isActive: boolean) => void;
  setRecording: (isRecording: boolean) => void;
  setMuted: (isMuted: boolean) => void;
  addTranscript: (entry: TranscriptUpdate) => void;
  setMetrics: (metrics: SessionMetrics) => void;
  clearSession: () => void;

  // DET actions
  setDETPhase: (phase: DETPhase | null) => void;
  setDETPhaseStartTime: (time: number | null) => void;
  setDETOverTime: (overTime: boolean) => void;
  addDETScore: (score: DETPhaseScore) => void;
  resetDETState: () => void;
}

export const useSessionStore = create<SessionStore>((set) => ({
  // Initial state
  sessionId: null,
  wsUrl: null,
  mode: null,
  state: 'INIT',
  isActive: false,
  isRecording: false,
  isMuted: false,
  transcript: [],
  metrics: null,

  // DET initial state
  detPhase: null,
  detPhaseStartTime: null,
  detPhaseOverTime: false,
  detScores: [],

  // Actions
  setSession: (sessionId, wsUrl, mode) => set({
    sessionId,
    wsUrl,
    mode,
    state: 'INIT',
    isActive: false,
    transcript: [],
    metrics: null,
    // Reset DET state when starting new session
    detPhase: null,
    detPhaseStartTime: null,
    detPhaseOverTime: false,
    detScores: [],
  }),

  setState: (state) => set({ state }),

  setActive: (isActive) => set({ isActive }),

  setRecording: (isRecording) => set({ isRecording }),

  setMuted: (isMuted) => set({ isMuted }),

  addTranscript: (entry) => set((state) => ({
    // Keep only the most recent entries to prevent memory bloat
    transcript: [...state.transcript.slice(-(UI_CONFIG.MAX_TRANSCRIPT_ENTRIES - 1)), entry],
  })),

  setMetrics: (metrics) => set({ metrics }),

  clearSession: () => set({
    sessionId: null,
    wsUrl: null,
    mode: null,
    state: 'INIT',
    isActive: false,
    isRecording: false,
    isMuted: false,
    transcript: [],
    metrics: null,
    // Clear DET state
    detPhase: null,
    detPhaseStartTime: null,
    detPhaseOverTime: false,
    detScores: [],
  }),

  // DET actions
  setDETPhase: (phase) => set({ detPhase: phase }),

  setDETPhaseStartTime: (time) => set({ detPhaseStartTime: time }),

  setDETOverTime: (overTime) => set({ detPhaseOverTime: overTime }),

  addDETScore: (score) => set((state) => ({
    detScores: [...state.detScores, score],
  })),

  resetDETState: () => set({
    detPhase: null,
    detPhaseStartTime: null,
    detPhaseOverTime: false,
    detScores: [],
  }),
}));
